import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../index';

// Key under which the last actively-selected location id is persisted, so the
// location selector and navigation can fall back to it on account-level pages
// (where the URL carries no :locationId).
export const LAST_LOCATION_STORAGE_KEY = 'rehabplanner.lastLocationId';

const readPersistedLastLocationId = (): string | null => {
  try {
    return globalThis.localStorage.getItem(LAST_LOCATION_STORAGE_KEY);
  } catch {
    return null;
  }
};

interface ApplicationState {
  isEditMode: boolean;
  hasUnsavedChanges: boolean;
  // Navigation hint only — never the source of truth. A location-scoped URL
  // (/locations/:locationId/...) always wins over this value.
  lastLocationId: string | null;
}

const initialState: ApplicationState = {
  isEditMode: false,
  hasUnsavedChanges: false,
  lastLocationId: readPersistedLastLocationId()
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    toggleEditMode: (state) => {
      state.isEditMode = !state.isEditMode;
    },
    setEditMode: (state, action: { payload: boolean }) => {
      state.isEditMode = action.payload;
      if (!action.payload) {
        state.hasUnsavedChanges = false;
      }
    },
    setHasUnsavedChanges: (state, action: { payload: boolean }) => {
      state.hasUnsavedChanges = action.payload;
    },
    setLastLocationId: (state, action: { payload: string | null }) => {
      state.lastLocationId = action.payload;
      try {
        if (action.payload) {
          globalThis.localStorage.setItem(LAST_LOCATION_STORAGE_KEY, action.payload);
        } else {
          globalThis.localStorage.removeItem(LAST_LOCATION_STORAGE_KEY);
        }
      } catch {
        // localStorage may be unavailable (private mode / quota) — the hint is
        // non-essential, so silently keep the in-memory value only.
      }
    }
  }
});

export const { toggleEditMode, setEditMode, setHasUnsavedChanges, setLastLocationId } =
  applicationSlice.actions;

export const selectLastLocationId = (state: RootState): string | null =>
  state.applicationSlice.lastLocationId;

export default applicationSlice.reducer;
